// ABOUTME: Profile tab for ChildProfilePage containing all 8 editable child fields
// ABOUTME: Auto-saves each field silently on blur via familyAPI.updateChild

import { useState, useEffect } from 'react';
import {
  Box, TextField, Grid, Avatar, Button, FormControl, FormHelperText, InputLabel, Select, MenuItem,
  CircularProgress, Paper, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Child } from '../../types/api';
import { familyAPI } from '../../services/api';
import NicknamesChipInput from '../common/NicknamesChipInput';
import AvatarUploadModal from '../AvatarUploadModal';
import { getAvatarUrl } from '../../utils/avatarUtils';
import naluV1Theme from '../../theme/theme.nalu-v1';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface ProfileTabProps {
  child: Child;
  onChildUpdated: (updated: Partial<Child>) => void;
  isUserFamilyOwner: boolean;
  /** When true, shows Save button and calls addChild instead of auto-saving */
  isCreateMode?: boolean;
  /** Required when isCreateMode is true */
  familyId?: number;
  /** Called with new childId after a successful create */
  onChildCreated?: (childId: number) => void;
}

export default function ProfileTab({ child, onChildUpdated, isUserFamilyOwner, isCreateMode, familyId, onChildCreated }: ProfileTabProps) {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [form, setForm] = useState({
    childName: child.name ?? '',
    aliases: child.aliases ?? [],
    birthYear: child.birthYear || 0,
    birthMonth: child.birthMonth ?? 0,
    gender: child.gender ?? '',
    mantra: child.mantra ?? '',
    familyLoveQuestion: child.familyLoveQuestion ?? '',
    familyNeedQuestion: child.familyNeedQuestion ?? '',
  });
  const [nameError, setNameError] = useState('');
  const [yearError, setYearError] = useState('');
  const [monthError, setMonthError] = useState('');
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Re-sync if the child prop changes (e.g. after create redirects to real childId)
  useEffect(() => {
    setForm({
      childName: child.name ?? '',
      aliases: child.aliases ?? [],
      birthYear: child.birthYear || 0,
      birthMonth: child.birthMonth ?? 0,
      gender: child.gender ?? '',
      mantra: child.mantra ?? '',
      familyLoveQuestion: child.familyLoveQuestion ?? '',
      familyNeedQuestion: child.familyNeedQuestion ?? '',
    });
    setNameError('');
    setYearError('');
    setMonthError('');
    // Intentionally scoped to child.childId only — this re-syncs the form when the
    // *identity* of the child changes (e.g. create-mode redirecting to a real childId),
    // not on every field update. Including the individual child.* fields would re-fire
    // this effect after each auto-save (parent re-renders with the saved value) and
    // clobber in-progress edits/error state on other fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child.childId]);

  async function saveField(overrides: Partial<typeof form> = {}) {
    // In create mode, fields are saved all at once via handleCreate
    if (isCreateMode) return;
    const merged = { ...form, ...overrides };
    if (!merged.childName.trim()) return;
    try {
      await familyAPI.updateChild(child.childId!, {
        childName: merged.childName.trim(),
        aliases: merged.aliases,
        birthYear: merged.birthYear,
        birthMonth: merged.birthMonth || undefined,
        gender: merged.gender || undefined,
        mantra: merged.mantra || undefined,
        familyLoveQuestion: merged.familyLoveQuestion || undefined,
        familyNeedQuestion: merged.familyNeedQuestion || undefined,
      });
      onChildUpdated({ name: merged.childName.trim(), aliases: merged.aliases });
    } catch (error) {
      console.error('Failed to save child field:', error);
    }
  }

  async function handleCreate() {
    if (!form.childName.trim()) {
      setNameError('Name is required');
      return;
    }
    if (!form.birthYear) {
      setYearError('Birth year is required');
      return;
    }
    if (!form.birthMonth) {
      setMonthError('Birth month is required');
      return;
    }
    if (!familyId) return;
    setIsSaving(true);
    try {
      const dateOfBirth = `${form.birthYear}-${String(form.birthMonth).padStart(2, '0')}-01`;
      const newChild = await familyAPI.addChild(familyId, {
        name: form.childName.trim(),
        dateOfBirth,
        aliases: form.aliases.length > 0 ? form.aliases : undefined,
        gender: form.gender || undefined,
        mantra: form.mantra || undefined,
        familyLoveQuestion: form.familyLoveQuestion || undefined,
        familyNeedQuestion: form.familyNeedQuestion || undefined,
      });
      onChildCreated?.(newChild.childId ?? 0);
    } catch (error) {
      console.error('Failed to create child:', error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAvatarSave(imageData: string | null) {
    setAvatarModalOpen(false);
    if (!child.childId) return;
    try {
      if (imageData === null) {
        await familyAPI.deleteChildAvatar(child.childId);
        onChildUpdated({ profileImageUrl: undefined });
      } else {
        const result = await familyAPI.uploadChildAvatar(child.childId, imageData);
        onChildUpdated({ profileImageUrl: result.avatarUrl ?? undefined });
      }
    } catch (error) {
      console.error('Failed to save child avatar:', error);
    }
  }

  const years = Array.from({ length: 25 }, (_, i) => currentYear - i);

  // nalu-v1's mockup groups these fields inside a flat card and splits them into
  // "Basics" and "Knowing this keiki" section headers (issue #1900). Built once here
  // so the fields themselves aren't duplicated between the Paper-wrapped (nalu-v1) and
  // plain (default/alt) render paths below.
  const fieldsGrid = (
    <Grid container spacing={2}>
      {/* Basics section header (nalu-v1 only) */}
      {isNaluV1Theme && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6">Basics</Typography>
        </Grid>
      )}
      {/* Name */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Child Name"
          value={form.childName}
          onChange={(e) => {
            const val = e.target.value;
            setForm((f) => ({ ...f, childName: val }));
            setNameError(val.trim() ? '' : 'Name is required');
          }}
          onBlur={() => saveField()}
          error={!!nameError}
          helperText={nameError}
          fullWidth
          required
          inputProps={{ 'aria-label': 'child name' }}
        />
      </Grid>

      {/* Birth Year */}
      <Grid size={{ xs: 12, sm: 3 }}>
        <FormControl fullWidth error={!!yearError}>
          <InputLabel id="profile-birth-year-label">{isCreateMode ? 'Birth Year *' : 'Birth Year'}</InputLabel>
          <Select
            labelId="profile-birth-year-label"
            value={form.birthYear || ''}
            label={isCreateMode ? 'Birth Year *' : 'Birth Year'}
            onChange={(e) => {
              const yr = Number(e.target.value) || 0;
              // Auto-clear birth month if now invalid (future month in current year)
              let bm = form.birthMonth;
              if (yr === currentYear && bm > currentMonth) bm = 0;
              setForm((f) => ({ ...f, birthYear: yr, birthMonth: bm }));
              if (yearError) setYearError('');
            }}
            onBlur={() => saveField()}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
          {yearError && <FormHelperText>{yearError}</FormHelperText>}
        </FormControl>
      </Grid>

      {/* Birth Month */}
      <Grid size={{ xs: 12, sm: 3 }}>
        <FormControl fullWidth error={!!monthError}>
          <InputLabel id="profile-birth-month-label">{isCreateMode ? 'Birth Month *' : 'Birth Month'}</InputLabel>
          <Select
            labelId="profile-birth-month-label"
            value={form.birthMonth || ''}
            label={isCreateMode ? 'Birth Month *' : 'Birth Month'}
            onChange={(e) => {
              setForm((f) => ({ ...f, birthMonth: Number(e.target.value) || 0 }));
              if (monthError) setMonthError('');
            }}
            onBlur={() => saveField()}
          >
            {MONTH_NAMES.map((m, i) => {
              const monthNum = i + 1;
              const isFuture = form.birthYear === currentYear && monthNum > currentMonth;
              return (
                <MenuItem key={m} value={monthNum} disabled={isFuture}>
                  {m}
                </MenuItem>
              );
            })}
          </Select>
          {monthError && <FormHelperText>{monthError}</FormHelperText>}
        </FormControl>
      </Grid>

      {/* Gender */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth>
          <InputLabel id="profile-gender-label">Gender (Optional)</InputLabel>
          <Select
            labelId="profile-gender-label"
            value={form.gender}
            label="Gender (Optional)"
            onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
            onBlur={() => saveField()}
          >
            <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
            <MenuItem value="Boy">Boy</MenuItem>
            <MenuItem value="Girl">Girl</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Nicknames */}
      <Grid size={{ xs: 12 }}>
        <NicknamesChipInput
          value={form.aliases}
          childName={form.childName}
          onChange={(aliases) => {
            setForm((f) => ({ ...f, aliases }));
            saveField({ aliases });
          }}
        />
      </Grid>

      {/* Mantra */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Personal Mantra (Optional)"
          value={form.mantra}
          onChange={(e) => setForm((f) => ({ ...f, mantra: e.target.value }))}
          onBlur={() => saveField()}
          multiline
          rows={2}
          inputProps={{ maxLength: 500, 'aria-label': 'mantra' }}
          fullWidth
        />
      </Grid>

      {/* Knowing this keiki section header (nalu-v1 only) */}
      {isNaluV1Theme && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" sx={{ mt: 1 }}>Knowing this keiki</Typography>
        </Grid>
      )}

      {/* Love question */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="How does this child show love to family? (Optional)"
          value={form.familyLoveQuestion}
          onChange={(e) => setForm((f) => ({ ...f, familyLoveQuestion: e.target.value }))}
          onBlur={() => saveField()}
          multiline
          rows={2}
          inputProps={{ maxLength: 500, 'aria-label': 'show love' }}
          fullWidth
        />
      </Grid>

      {/* Need question */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="What does this child need from family? (Optional)"
          value={form.familyNeedQuestion}
          onChange={(e) => setForm((f) => ({ ...f, familyNeedQuestion: e.target.value }))}
          onBlur={() => saveField()}
          multiline
          rows={2}
          inputProps={{ maxLength: 500, 'aria-label': 'need from family' }}
          fullWidth
        />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      {/* Avatar */}
      {isUserFamilyOwner && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            src={child.profileImageUrl ? getAvatarUrl(child.profileImageUrl) : undefined}
            sx={{
              width: 80,
              height: 80,
              ...(isNaluV1Theme && {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }),
            }}
          >
            {child.name?.[0]?.toUpperCase()}
          </Avatar>
          <Button
            variant="outlined"
            onClick={() => setAvatarModalOpen(true)}
            aria-label={child.profileImageUrl ? 'edit avatar' : 'add avatar'}
          >
            {child.profileImageUrl ? 'Edit Avatar' : 'Add Avatar'}
          </Button>
        </Box>
      )}

      {/* nalu-v1's mockup groups these fields inside a flat card (issue #1900) — Paper
          picks up the theme's global flat-cream-fill + hairline-border styling
          automatically, so no manual bgcolor/border override is needed here. */}
      {isNaluV1Theme ? <Paper sx={{ p: 3 }}>{fieldsGrid}</Paper> : fieldsGrid}

      {isCreateMode && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={isSaving}
            aria-label="save new child"
          >
            {isSaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Box>
      )}

      {isUserFamilyOwner && !isCreateMode && (
        <AvatarUploadModal
          isOpen={avatarModalOpen}
          onClose={() => setAvatarModalOpen(false)}
          onSave={handleAvatarSave}
          currentAvatarUrl={child.profileImageUrl ?? undefined}
          entityType="child"
          entityId={child.childId}
        />
      )}
    </Box>
  );
}
